// SPEC-46 · the words of 1.3 Antecedentes de lesão around the protocol's:
// the views of the mannequin, the zone's panel, the list of zones marked,
// the note. PT first (BRAND.md), FR and EN key for key. The zones and the
// lesions are src/i18n/bilanProtocol*.ts.

import type { Words } from "../shell";

export const bodyMapPt = {
  views: "Vistas do corpo",
  /** The views control, in its order: front, her left side, back, her right side. */
  viewNames: { frente: "Frente", esq: "Esq.", costas: "Costas", dir: "Dir." },
  /** The name of the zones' group on the mannequin. */
  zones: "Zonas do corpo",
  /** Under the mannequin while nothing is marked. */
  hint: "Toca numa zona do corpo",
  /** Under the mannequin: « 2 zonas · 3 lesões ». */
  counts: (zones: number, lesions: number) => `${zones === 1 ? "1 zona" : `${zones} zonas`} · ${lesions === 1 ? "1 lesão" : `${lesions} lesões`}`,
  close: "Fechar",
  other: "Outra lesão nesta zona…",
  otherLabel: "Outra lesão nesta zona",
  /** The name of the list of zones marked. */
  marked: "Zonas marcadas",
  /** A zone's lesions in the list, in the order they were marked. */
  lesions: (words: readonly string[]) => words.join(" · "),
  /** A lesion typed by hand: « Outra: Fissura do menisco externo ». */
  typed: (other: string, text: string) => `${other}: ${text}`,
  none: "Sem antecedentes registados. Toca numa zona do corpo para adicionar.",
  /** SPEC-63 · 1.4's pains on the same mannequin: a written pain per zone. */
  pains: {
    hint: "Toca na zona que dói",
    /** Under the mannequin: « 2 zonas com dor ». */
    counts: (zones: number) => (zones === 1 ? "1 zona com dor" : `${zones} zonas com dor`),
    field: "Descreve a dor nesta zona…",
    /** The field's accessible name: « Dor · Joelho esq. ». */
    fieldLabel: (zone: string) => `Dor · ${zone}`,
    /** The name of the list of zones with a pain. */
    listed: "Zonas com dor",
    none: "Sem dores registadas. Toca numa zona do corpo para adicionar.",
  },
} as const;

export const bodyMapFr: Words<typeof bodyMapPt> = {
  views: "Vues du corps",
  viewNames: { frente: "Face", esq: "G", costas: "Dos", dir: "D" },
  zones: "Zones du corps",
  hint: "Touche une zone du corps",
  counts: (zones: number, lesions: number) => `${zones === 1 ? "1 zone" : `${zones} zones`} · ${lesions === 1 ? "1 blessure" : `${lesions} blessures`}`,
  close: "Fermer",
  other: "Autre blessure dans cette zone…",
  otherLabel: "Autre blessure dans cette zone",
  marked: "Zones marquées",
  lesions: (words: readonly string[]) => words.join(" · "),
  typed: (other: string, text: string) => `${other} : ${text}`,
  none: "Aucun antécédent enregistré. Touche une zone du corps pour en ajouter.",
  pains: {
    hint: "Touche la zone douloureuse",
    counts: (zones: number) => (zones === 1 ? "1 zone douloureuse" : `${zones} zones douloureuses`),
    field: "Décris la douleur dans cette zone…",
    fieldLabel: (zone: string) => `Douleur · ${zone}`,
    listed: "Zones douloureuses",
    none: "Aucune douleur enregistrée. Touche une zone du corps pour en ajouter une.",
  },
};

export const bodyMapEn: Words<typeof bodyMapPt> = {
  views: "Body views",
  viewNames: { frente: "Front", esq: "L.", costas: "Back", dir: "R." },
  zones: "Body zones",
  hint: "Tap a zone of the body",
  counts: (zones: number, lesions: number) => `${zones === 1 ? "1 zone" : `${zones} zones`} · ${lesions === 1 ? "1 injury" : `${lesions} injuries`}`,
  close: "Close",
  other: "Other injury in this zone…",
  otherLabel: "Other injury in this zone",
  marked: "Zones marked",
  lesions: (words: readonly string[]) => words.join(" · "),
  typed: (other: string, text: string) => `${other}: ${text}`,
  none: "No injury history recorded. Tap a zone of the body to add one.",
  pains: {
    hint: "Tap the zone that hurts",
    counts: (zones: number) => (zones === 1 ? "1 painful zone" : `${zones} painful zones`),
    field: "Describe the pain in this zone…",
    fieldLabel: (zone: string) => `Pain · ${zone}`,
    listed: "Painful zones",
    none: "No pain recorded. Tap a zone of the body to add one.",
  },
};
